import validateData from '@/helpers/validationHelpers/validationSchema';
import { CustomFormHelperText } from '@/module/all-screenings/component/ViewPatientRegistration/ModalInputServices';
import { TYPE_MODAL } from '@/module/telecounsel-appointments/constants';
import CustomDate from '@/new-components/CustomDate';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import CustomModalTitle from '@/new-components/CustomModal/CustomModalTitle';
import {
  Box,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  withStyles,
} from '@material-ui/core';
import { get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

const CustomInputLabel = withStyles((theme) => ({
  root: {
    color: '#666666',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))(InputLabel);

const CustomTextField = withStyles({
  root: {
    fontSize: 16,
    width: '100%',

    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      padding: '8px 5px',
      fontSize: 16,
    },
    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& .MuiFormHelperText-root': {
      alignItems: 'center',
      display: 'flex',
      fontFamily: 'OpenSans',
      fontSize: 12,
      gap: 6,
      marginLeft: 2,

      '& .MuiSvgIcon-root': {
        fontSize: 'unset',
      },
    },
    '& .MuiInputBase-multiline': {
      height: 'auto',
      paddingLeft: 0,
      paddingTop: 10,
      paddingBottom: 10,
      lineHeight: 1.5,

      '& .MuiOutlinedInput-input': {
        marginRight: 8,
      },
    },
    '& .MuiInputBase-adornedStart': {
      '& .MuiTypography-root': {
        fontSize: 16,
        color: '#999999',
      },
      '& .MuiInputAdornment-positionStart': {
        marginRight: 0,
      },
      '& input': {
        marginLeft: 0,
      },
    },
  },
})(TextField);

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    height: 'calc(100vh - 140px)',
    overflow: 'auto',
  },
  stepsControlWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
}));

const ModalAddOrEditPromoCode = ({
  typeModal,
  onClose,
  onSubmit,
  selectedItem,
  loading,
  isCounselling,
}) => {
  const classes = useStyles();
  const [errors, setErrors] = useState({});

  const [formValue, setFormValue] = useState({
    startDate: null,
    endDate: null,
    name: '',
    code: '',
    quota: 0,
    promoCodeType: isCounselling ? 'TeleCounsel' : 'Teledoc',
    description: '',
    price: 0,
  });

  useEffect(() => {
    if (typeModal === TYPE_MODAL.Edit) setFormValue(selectedItem);
  }, [typeModal, selectedItem]);

  const isMatchedPattern = (key, value) => {
    switch (key) {
      case 'quota':
        const reg = /^\d+$/;
        return value.match(reg) || value === '';

      default:
        return true;
    }
  };

  const handleChangeForm = (key) => (e) => {
    let value;
    switch (key) {
      case 'startDate':
        const newStartDate = moment(e).startOf('date').format();
        value = newStartDate;
        break;
      case 'endDate':
        const newEndDate = moment(e).endOf('date').format();
        value = newEndDate;
        break;
      case 'price':
        value = e.value;
        setFormValue({ ...formValue, [key]: Number(value) });
        return;
      default:
        value = get(e, 'target.value');
    }
    if (!isMatchedPattern(key, value)) return;

    setFormValue({ ...formValue, [key]: value });
  };

  const handleSubmit = async () => {
    try {
      await validateData('promotionSchema', formValue, (validData) => {
        onSubmit(validData);
      });
    } catch (errs) {
      setErrors(errs);
    }
  };

  const isEmptyParams = useMemo(() => {
    const filterKeys = [
      'code',
      'name',
      'quota',
      'startDate',
      'endDate',
      'description',
      // 'price',
    ];

    if (isCounselling) {
      filterKeys.push('price');
    }

    const checkValid = filterKeys.some((key) => !formValue[key]);
    return checkValid;
  }, [formValue, isCounselling]);

  return (
    <Paper square elevation={0}>
      <CustomModalTitle onClose={onClose}>
        {typeModal === TYPE_MODAL.Create
          ? isCounselling
            ? 'Create new company code'
            : 'Create new promotion'
          : isCounselling
          ? 'Edit company code'
          : 'Edit promotion'}
      </CustomModalTitle>
      <main className={classes.mainWrapper}>
        <Box p={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomInputLabel>Code</CustomInputLabel>
              <CustomTextField
                fullWidth
                variant="outlined"
                placeholder="Enter code"
                error={!!errors.code}
                helperText={errors.code}
                value={formValue.code || ''}
                onChange={handleChangeForm('code')}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInputLabel>Name</CustomInputLabel>
              <CustomTextField
                fullWidth
                variant="outlined"
                placeholder="Enter name"
                error={!!errors.name}
                helperText={errors.name}
                value={formValue.name || ''}
                onChange={handleChangeForm('name')}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInputLabel>Quota</CustomInputLabel>
              <CustomTextField
                fullWidth
                variant="outlined"
                placeholder="Enter quota"
                error={!!errors.quota}
                helperText={errors.quota}
                value={formValue.quota || ''}
                onChange={handleChangeForm('quota')}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomInputLabel>Start date</CustomInputLabel>
              <CustomDate
                selectedDate={
                  formValue.startDate ? moment(formValue.startDate) : ''
                }
                minDate={moment()}
                maxDate={formValue.endDate}
                isFullWidth
                placeholder="DD/MM/YYYY"
                clearable={false}
                onChange={handleChangeForm('startDate')}
                style={{ height: 48, fontSize: 16 }}
              />
              {errors.startDate && (
                <CustomFormHelperText error>
                  {errors.startDate}
                </CustomFormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <CustomInputLabel>End date</CustomInputLabel>
              <CustomDate
                selectedDate={
                  formValue.endDate ? moment(formValue.endDate) : ''
                }
                minDate={formValue.startDate}
                isFullWidth
                placeholder="DD/MM/YYYY"
                clearable={false}
                onChange={handleChangeForm('endDate')}
                style={{ height: 48, fontSize: 16 }}
              />
              {errors.endDate && (
                <CustomFormHelperText error>
                  {errors.endDate}
                </CustomFormHelperText>
              )}
            </Grid>
            {isCounselling && (
              <Grid item xs={12}>
                <CustomInputLabel>Price</CustomInputLabel>

                <NumberFormat
                  fullWidth
                  customInput={CustomTextField}
                  variant="outlined"
                  placeholder="0"
                  onValueChange={handleChangeForm('price')}
                  allowNegative={false}
                  value={formValue.price || ''}
                  decimalScale={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <CustomInputLabel>Description</CustomInputLabel>
              <CustomTextField
                rows={5}
                multiline
                fullWidth
                variant="outlined"
                placeholder="Enter description"
                value={formValue.description || ''}
                onChange={handleChangeForm('description')}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>
          </Grid>
        </Box>
      </main>
      <Box
        display="flex"
        justifyContent="flex-end"
        py={1.75}
        px={1.25}
        gridGap={16}
        borderTop={'1px solid #E0E0E0'}
      >
        <CustomLoadingButton
          style={{ width: '100%' }}
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </CustomLoadingButton>

        <CustomLoadingButton
          style={{ width: '100%' }}
          variant="contained"
          color="primary"
          loading={loading}
          disabled={loading || isEmptyParams}
          onClick={handleSubmit}
        >
          {typeModal === TYPE_MODAL.Create ? 'Create' : 'Save'}
        </CustomLoadingButton>
      </Box>
    </Paper>
  );
};

ModalAddOrEditPromoCode.propTypes = {
  typeModal: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  selectedItem: PropTypes.object,
};

ModalAddOrEditPromoCode.defaultProps = {
  typeModal: TYPE_MODAL.Create,
  open: false,
  onClose: () => console.warn('Not implemented yet'),
  onSubmit: () => console.warn('Not implemented yet'),
  selectedItem: null,
};

export default ModalAddOrEditPromoCode;
